import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Row, Col, FormGroup, Label, Input, FormFeedback, Button, Alert } from 'reactstrap';
import {
  DEFAULT_CENTER, TILE_ATTRIBUTION, TILE_URL, currentPosition, embedUrl, geocodeAddress, hasLocation,
  roundCoordinate,
} from '../utils/maps';

const sameSpot = (a, b) =>
  a && b && Number(a.latitude) === Number(b.latitude) && Number(a.longitude) === Number(b.longitude);

/** A map pin drawn over the middle of the map; its tip is the exact point that gets saved. */
const CentrePin = () => (
  <svg
    width="36" height="48" viewBox="0 0 36 48" aria-hidden="true"
    style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 1 }}
  >
    <path d="M18 47C18 47 34 28.5 34 17A16 16 0 0 0 2 17C2 28.5 18 47 18 47Z" fill="#d93025" stroke="#a50e0e" strokeWidth="2" />
    <circle cx="18" cy="17" r="6" fill="#fff" />
  </svg>
);

/**
 * Where the business is: the pin stays in the middle and the map moves under it. Every move the
 * owner makes (drag, zoom, scroll) sets the location to the point under the pin. If the map cannot be drawn, OpenStreetMap's embed shows the pin, and the
 * coordinates are typed or taken from the browser.
 */
const LocationPicker = ({ latitude, longitude, onChange, required = false, errors = {} }) => {
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [mapFailed, setMapFailed] = useState(false);
  const interactive = !mapFailed;

  const mapElement = useRef(null);
  const map = useRef(null);
  const lastFromMap = useRef(null);
  const movingInCode = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const located = hasLocation(latitude, longitude);

  useEffect(() => {
    if (!interactive || !mapElement.current) return undefined;
    const start = located ? { latitude, longitude } : DEFAULT_CENTER;
    let created;
    try {
      // Capped inertia, so a quick flick glides a little instead of throwing the pin across the country.
      created = L.map(mapElement.current, { zoomControl: true, attributionControl: true, inertiaMaxSpeed: 1500 })
        .setView([Number(start.latitude), Number(start.longitude)], located ? 17 : 12);
      L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(created);
    } catch (e) {
      setMapFailed(true);
      return undefined;
    }
    // Any move the owner makes sets the location, live while dragging; moves made in code (opening on
    // the default centre, following a search) do not.
    const report = () => {
      if (movingInCode.current) return;
      const centre = created.getCenter();
      const next = { latitude: roundCoordinate(centre.lat), longitude: roundCoordinate(centre.lng) };
      if (sameSpot(next, lastFromMap.current)) return;
      lastFromMap.current = next;
      onChangeRef.current(next);
    };
    created.on('move', report);
    created.on('moveend', () => {
      if (movingInCode.current) {
        // Done following the inputs: keep their exact values rather than the rounded centre.
        movingInCode.current = false;
        return;
      }
      report();
    });
    map.current = created;
    return () => {
      created.remove();
      map.current = null;
    };
    // The map is built once; later coordinates reach it through the effect below.
  }, [interactive]);

  // Coordinates from a search, the browser or the inputs move the map; ones the map sent do not.
  useEffect(() => {
    if (!map.current || !located) return;
    if (sameSpot({ latitude, longitude }, lastFromMap.current)) return;
    movingInCode.current = true;
    map.current.setView([Number(latitude), Number(longitude)], Math.max(map.current.getZoom(), 17));
  }, [latitude, longitude, located]);

  const run = async (kind, find) => {
    setBusy(kind);
    setMessage(null);
    try {
      const found = await find();
      onChange({ latitude: found.latitude, longitude: found.longitude });
      if (found.label) setMessage({ color: 'success', text: `Found ${found.label}. Drag the map to put the pin on your door.` });
    } catch (e) {
      setMessage({ color: 'danger', text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const searchAddress = () => {
    const value = address.trim();
    if (!value) {
      setMessage({ color: 'danger', text: 'Type the business address first.' });
      return;
    }
    run('address', () => geocodeAddress(value));
  };

  const handleCoordinate = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    onChange({
      latitude: field === 'latitude' ? numValue : latitude,
      longitude: field === 'longitude' ? numValue : longitude,
    });
  };

  const validateLatitude = (val) => {
    if (val === '' || val === null || val === undefined) {
      return required ? 'Latitude is required' : null;
    }
    const num = parseFloat(val);
    if (isNaN(num) || num < -90 || num > 90) {
      return 'Latitude must be between -90 and 90';
    }
    return null;
  };

  const validateLongitude = (val) => {
    if (val === '' || val === null || val === undefined) {
      return required ? 'Longitude is required' : null;
    }
    const num = parseFloat(val);
    if (isNaN(num) || num < -180 || num > 180) {
      return 'Longitude must be between -180 and 180';
    }
    return null;
  };

  const latError = errors.latitude || validateLatitude(latitude);
  const lngError = errors.longitude || validateLongitude(longitude);

  return (
    <div className="mb-3">
      <Label>Location {required && <span className="text-danger">*</span>}</Label>

      <div className="d-flex gap-2 mb-2">
          <Input
            type="text"
            placeholder="Search the business address"
            aria-label="Business address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => {
              // Enter searches instead of submitting the whole form.
              if (e.key === 'Enter') {
                e.preventDefault();
                searchAddress();
              }
            }}
            disabled={busy !== null}
          />
          <Button type="button" color="primary" onClick={searchAddress} disabled={busy !== null}>
            {busy === 'address' ? 'Searching…' : 'Search'}
          </Button>
      </div>

      <div
        className="position-relative border rounded overflow-hidden mb-2"
        style={{ height: 320, background: '#e9ecef' }}
      >
        {interactive ? (
          <>
            {/* zIndex 0 keeps Leaflet's layers (z-index 400+) inside the map, under the pin. */}
            <div ref={mapElement} data-testid="location-map" style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
            <CentrePin />
          </>
        ) : located ? (
          <iframe
            title="Business location"
            src={embedUrl(latitude, longitude)}
            style={{ border: 0, width: '100%', height: '100%' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="h-100 d-flex align-items-center justify-content-center text-muted p-3 text-center">
            Use your current location or type the coordinates to see the business on the map.
          </div>
        )}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button
          type="button" color="secondary" outline size="sm"
          onClick={() => run('gps', currentPosition)}
          disabled={busy !== null}
        >
          {busy === 'gps' ? 'Finding you…' : 'Use my current location'}
        </Button>
        {interactive && <small className="text-muted">Move the map so the pin sits on the business; the location follows the pin.</small>}
      </div>

      {message && <Alert color={message.color} className="py-2">{message.text}</Alert>}

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label for="latitude" className="small text-muted">Latitude</Label>
            <Input
              id="latitude"
              name="latitude"
              type="number"
              step="any"
              min="-90"
              max="90"
              value={latitude === '' || latitude === null ? '' : latitude}
              onChange={(e) => handleCoordinate('latitude', e.target.value)}
              invalid={!!latError}
              required={required}
            />
            {latError && <FormFeedback>{latError}</FormFeedback>}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="longitude" className="small text-muted">Longitude</Label>
            <Input
              id="longitude"
              name="longitude"
              type="number"
              step="any"
              min="-180"
              max="180"
              value={longitude === '' || longitude === null ? '' : longitude}
              onChange={(e) => handleCoordinate('longitude', e.target.value)}
              invalid={!!lngError}
              required={required}
            />
            {lngError && <FormFeedback>{lngError}</FormFeedback>}
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default LocationPicker;
